import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowRight, ChevronLeft, Minus, Pencil, RefreshCw, Rotate3d, TriangleAlert,
} from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import { useAssets as useOpsAssets, useTelemetry } from '@/lib/api/operations'
import {
  useAsset, useAssetEvents, useTransitionAsset, useUpdateAsset,
  type AssetUpdate, type LifecycleState,
} from '@/lib/api/twin'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { PackBadge } from '@/ui/pack-badge'
import { EmptyState, LoadingBlock } from '@/ui/states'
import { LIFECYCLE, lcMeta, TYPE_OPTIONS, typeLabel } from './meta'

const num = (v: string) => (v.trim() === '' ? undefined : Number(v))

export function AssetDetailPage() {
  const { role } = useRole()
  const { site, siteId } = useCurrentSite()
  const { assetId } = useParams()
  const nav = useNavigate()
  const id = siteId ?? ''
  const canEdit = role === 'assessor' || role === 'owner'

  const asset = useAsset(assetId)
  const events = useAssetEvents(assetId)
  useOpsAssets(id) // warm the SCADA sync so telemetry attaches to the register asset
  const a = asset.data
  const tk = a?.telemetry_key || a?.asset_id
  const tele = useTelemetry(a ? tk : undefined)
  const transition = useTransitionAsset(id)
  const update = useUpdateAsset(id)

  const [target, setTarget] = useState<LifecycleState | ''>('')
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)

  if (asset.isLoading) return <Card><LoadingBlock lines={6} /></Card>
  if (asset.isError || !a) {
    return (
      <EmptyState kind="error" title="Asset not found"
        action={<Link to={`/site/${id}/assets`} className="text-sm font-semibold text-primary">Back to Assets</Link>}>
        This asset doesn’t exist or isn’t in your workspace.
      </EmptyState>
    )
  }

  const lc = lcMeta(a.lifecycle_state)
  const curIdx = LIFECYCLE.findIndex((l) => l.id === a.lifecycle_state)
  const placed = a.pos_x != null && a.pos_y != null
  const signals = tele.data?.signals ?? {}
  const primaryKey = Object.keys(signals)[0]
  const feeds = primaryKey ? signals[primaryKey]?.feeds : null

  const doTransition = () => {
    if (!target) return
    transition.mutate({ assetId: a.asset_id, to_state: target, note: note || undefined },
      { onSuccess: () => { setTarget(''); setNote('') } })
  }

  return (
    <>
      <Link to={`/site/${id}/assets`} className="mb-2 inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-3.5" /> Assets
      </Link>
      <PageHeader
        title={`${a.asset_id} · ${a.name ?? ''}`}
        subtitle={
          <>
            {site && <PackBadge pack={site.pack} />}
            <span>{typeLabel(a.asset_type)}</span>
            {a.location && (<><span className="text-faint">·</span><span>{a.location}</span></>)}
            <RiskChip label={lc.label} tone={lc.tone} />
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => nav(`/site/${id}/twin`)}><Rotate3d /> View in 3D twin</Button>
            {canEdit && <Button variant="ghost" onClick={() => setEditing((v) => !v)}><Pencil /> {editing ? 'Close' : 'Edit'}</Button>}
          </>
        }
      />

      {a.lifecycle_state === 'impaired' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-flare/40 bg-flare/[0.08] px-3 py-2.5 text-[13px]">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-flare" />
          <span><b>Impaired</b> — this asset is flagged below threshold. Resolve the linked obligation before returning it to operational.</span>
        </div>
      )}

      {editing && canEdit && <EditForm asset={a} onSave={(patch) => update.mutate({ assetId: a.asset_id, patch }, { onSuccess: () => setEditing(false) })} saving={update.isPending} />}

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-5">
          {/* lifecycle */}
          <Card>
            <KLabel className="mb-3">Lifecycle</KLabel>
            <div className="flex flex-wrap items-center gap-y-2">
              {LIFECYCLE.map((l, i) => (
                <div key={l.id} className="flex items-center">
                  {i > 0 && <span className="mx-1 h-0.5 w-5" style={{ background: i <= curIdx ? lc.tone : 'rgb(var(--border))' }} />}
                  <span
                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    style={
                      i === curIdx
                        ? { color: l.tone, background: `color-mix(in srgb, ${l.tone} 16%, transparent)` }
                        : { color: i < curIdx ? 'rgb(var(--muted-foreground))' : 'rgb(var(--faint))' }
                    }
                  >
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
            {canEdit ? (
              <div className="mt-4 flex flex-wrap items-end gap-2.5">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-faint">Transition to</label>
                  <select
                    value={target}
                    onChange={(e) => setTarget(e.target.value as LifecycleState)}
                    className="h-8 rounded-md border border-input bg-surface-2 px-2 text-[13px] outline-none focus:border-primary"
                  >
                    <option value="">Choose state…</option>
                    {LIFECYCLE.filter((l) => l.id !== a.lifecycle_state).map((l) => (
                      <option key={l.id} value={l.id}>{l.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="h-8 min-w-[180px] flex-1 rounded-md border border-input bg-surface-2 px-3 text-[13px] outline-none focus:border-primary"
                />
                <Button size="sm" onClick={doTransition} disabled={!target || transition.isPending}>
                  <RefreshCw /> {transition.isPending ? 'Saving…' : 'Transition'}
                </Button>
                {a.lifecycle_state !== 'decommissioned' && (
                  <Button size="sm" variant="ghost"
                    onClick={() => transition.mutate({ assetId: a.asset_id, to_state: 'decommissioned' })}>
                    <Minus /> Decommission
                  </Button>
                )}
              </div>
            ) : (
              <p className="mt-3 text-[12px] text-faint">Lifecycle changes are made by an assessor or owner.</p>
            )}
          </Card>

          {/* live telemetry */}
          <Card>
            <div className="mb-3 flex items-center gap-2">
              <KLabel>Live telemetry</KLabel>
              {primaryKey && (
                <span className="inline-flex items-center gap-1.5 rounded border border-input px-2 py-0.5 text-[11px] text-clear">
                  <span className="size-1.5 animate-pulse rounded-full bg-clear" /> streaming
                </span>
              )}
            </div>
            {primaryKey ? (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  {Object.entries(signals).slice(0, 3).map(([k, s]) => (
                    <div key={k} className="rounded-lg border bg-surface-2 p-3">
                      <div className="text-[11px] text-faint">{s.label}</div>
                      <div className="mt-1 font-display text-[20px] font-semibold tabular-nums" style={s.over ? { color: 'rgb(var(--flare))' } : undefined}>
                        {s.current}<span className="ml-1 text-[11px] font-medium text-muted-foreground">{s.unit}</span>
                      </div>
                      <div className="mt-0.5 text-[10.5px] text-faint">threshold {s.threshold}{s.over ? ' · over' : ''}</div>
                    </div>
                  ))}
                </div>
                {feeds && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                    <span className="rounded bg-surface-2 px-2 py-1">{signals[primaryKey].label}</span>
                    <ArrowRight className="size-3.5" />
                    <span className="rounded bg-surface-2 px-2 py-1">{feeds[0]}</span>
                    <ArrowRight className="size-3.5" />
                    <span className="rounded px-2 py-1 font-semibold text-primary" style={{ background: 'rgb(var(--primary) / 0.12)' }}>{feeds[1]}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[12.5px] text-faint">
                {a.lifecycle_state === 'decommissioned'
                  ? 'A decommissioned asset does not stream telemetry.'
                  : 'Live telemetry attaches once this asset is streaming in Operations.'}
              </p>
            )}
          </Card>

          {/* history */}
          <Card>
            <KLabel className="mb-3">Lifecycle history</KLabel>
            {events.data && events.data.length > 0 ? (
              <div className="flex flex-col gap-2">
                {events.data.map((e) => {
                  const to = lcMeta(e.to_state)
                  return (
                    <div key={e.event_id} className="flex items-center gap-2.5 text-[12.5px]">
                      <RiskChip label={to.label} tone={to.tone} />
                      <span className="text-muted-foreground">
                        {e.from_state ? `from ${lcMeta(e.from_state).label}` : 'registered'}
                        {e.note ? ` — ${e.note}` : ''}
                      </span>
                      <span className="ml-auto text-[11px] text-faint">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-[12.5px] text-faint">No transitions recorded yet.</p>
            )}
          </Card>
        </div>

        {/* right column */}
        <div className="flex flex-col gap-5">
          <Card>
            <KLabel className="mb-3">Attributes</KLabel>
            {(
              [
                ['Type', typeLabel(a.asset_type)],
                ['Manufacturer', a.manufacturer ?? '—'],
                ['Capacity', a.capacity ?? '—'],
                ['Commissioned', a.commissioned_date ?? '—'],
                ['Location', a.location ?? '—'],
                ['Telemetry key', a.telemetry_key ?? a.asset_id],
              ] as [string, string][]
            ).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b py-1.5 last:border-0">
                <span className="text-[12px] text-faint">{k}</span>
                <span className="text-[12.5px] font-medium">{v}</span>
              </div>
            ))}
          </Card>
          <Card>
            <KLabel className="mb-2">Position on site plan</KLabel>
            {placed ? (
              <p className="mb-3 text-[12px] text-muted-foreground tabular-nums">
                x {a.pos_x}, y {a.pos_y} · {a.width ?? '?'}×{a.depth ?? '?'}×{a.height ?? '?'} · floor {a.floor ?? 0}
              </p>
            ) : (
              <p className="mb-3 text-[12px] text-faint">Not yet placed — open the twin editor to position it.</p>
            )}
            <Button size="sm" variant="secondary" className="w-full justify-center" onClick={() => nav(`/site/${id}/twin`)}>
              <Rotate3d /> Open 3D twin
            </Button>
          </Card>
        </div>
      </div>
    </>
  )
}

function EditForm({ asset, onSave, saving }: { asset: import('@/lib/api/twin').Asset; onSave: (p: AssetUpdate) => void; saving: boolean }) {
  const [f, setF] = useState({
    name: asset.name ?? '', asset_type: asset.asset_type ?? TYPE_OPTIONS[0].id,
    manufacturer: asset.manufacturer ?? '', capacity: asset.capacity ?? '', location: asset.location ?? '',
    pos_x: asset.pos_x?.toString() ?? '', pos_y: asset.pos_y?.toString() ?? '',
    width: asset.width?.toString() ?? '', depth: asset.depth?.toString() ?? '', height: asset.height?.toString() ?? '',
  })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }))
  const inp = 'h-8 w-full rounded-md border border-input bg-surface-2 px-2.5 text-[13px] outline-none focus:border-primary'
  const save = () => onSave({
    name: f.name, asset_type: f.asset_type, manufacturer: f.manufacturer || undefined,
    capacity: f.capacity || undefined, location: f.location || undefined,
    pos_x: num(f.pos_x), pos_y: num(f.pos_y), width: num(f.width), depth: num(f.depth), height: num(f.height),
  })
  return (
    <Card className="mb-5">
      <KLabel className="mb-3">Edit asset</KLabel>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-[11px] font-medium text-faint">Name<input className={inp} value={f.name} onChange={set('name')} /></label>
        <label className="text-[11px] font-medium text-faint">Type
          <select className={inp} value={f.asset_type} onChange={set('asset_type')}>
            {TYPE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-medium text-faint">Manufacturer<input className={inp} value={f.manufacturer} onChange={set('manufacturer')} /></label>
        <label className="text-[11px] font-medium text-faint">Capacity<input className={inp} value={f.capacity} onChange={set('capacity')} /></label>
        <label className="text-[11px] font-medium text-faint">Location<input className={inp} value={f.location} onChange={set('location')} /></label>
        <div />
        <label className="text-[11px] font-medium text-faint">Pos X<input className={inp} value={f.pos_x} onChange={set('pos_x')} inputMode="decimal" /></label>
        <label className="text-[11px] font-medium text-faint">Pos Y<input className={inp} value={f.pos_y} onChange={set('pos_y')} inputMode="decimal" /></label>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[11px] font-medium text-faint">W<input className={inp} value={f.width} onChange={set('width')} inputMode="decimal" /></label>
          <label className="text-[11px] font-medium text-faint">D<input className={inp} value={f.depth} onChange={set('depth')} inputMode="decimal" /></label>
          <label className="text-[11px] font-medium text-faint">H<input className={inp} value={f.height} onChange={set('height')} inputMode="decimal" /></label>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </Card>
  )
}
